/**
 * React Query mutations for evaluation status transitions
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Evaluation } from '@/types/evaluation.types';
import { storageService } from '@/services/local-storage';
import {
  activateEvaluation,
  concludeEvaluation,
  closeEvaluation,
  reactivateEvaluation,
} from '@/services/evaluation-status';

/**
 * Mutation hook for activating an evaluation
 */
export function useActivateEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (evaluation: Evaluation) => {
      const updated = activateEvaluation(evaluation);
      storageService.saveEvaluation(updated);
      return updated;
    },
    onSuccess: (updated) => {
      // Invalidate evaluation queries to refresh UI
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['evaluation', updated.id] });
    },
  });
}

/**
 * Mutation hook for concluding an evaluation
 */
export function useConcludeEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (evaluation: Evaluation) => {
      const updated = concludeEvaluation(evaluation);
      storageService.saveEvaluation(updated);
      return updated;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['evaluation', updated.id] });
    },
  });
}

/**
 * Mutation hook for closing an evaluation with optional reason
 */
export function useCloseEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      evaluation,
      reason,
    }: {
      evaluation: Evaluation;
      reason?: string;
    }) => {
      const updated = closeEvaluation(evaluation, reason);
      storageService.saveEvaluation(updated);
      return updated;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['evaluation', updated.id] });
    },
  });
}

/**
 * Mutation hook for reactivating an evaluation
 */
export function useReactivateEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (evaluation: Evaluation) => {
      const updated = reactivateEvaluation(evaluation);
      storageService.saveEvaluation(updated);
      return updated;
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['evaluations'] });
      queryClient.invalidateQueries({ queryKey: ['evaluation', updated.id] });
    },
  });
}
